<template lang="pug">
v-row#suus__post-form.my-4.mt-sm-8(no-gutters)
  v-col(cols="12", v-if="!isEdit")
    PostFormHeader
  v-col(cols="12" v-if="warning")
    WarningAlert(:message="warning")
  template(v-if="!disabled")
    v-col(cols="12")
      ForumWysiwygWithUploader(
        v-model="postText",
        :getMentionables="getMentionableFunc"
        :inline="inline"
        :load-attachment="loadAttachment"
        @attachments-valid="updateAttachmentsValid"
      )
       template(v-slot:default="slotProps")
          div(style="width:100%")
            GalleryAddon(
              v-if="galleryActive"
              @valid="setGalleryValididity"
              :initial-show-uploader="initialShowUploader"
              :post="value"
              :register-wysiwyg-addon="slotProps.registerAddon"
              :register-post-form-addon="registerGalleryAddon")

    v-col(cols="12")
      PostFormButton(:inline="inline" :disabled="buttonDisabled || isSaving" @click="savePost")
    v-col(cols="12")
      PostFormError(:error="errorText")
  template(v-else)
    v-col(cols="12")
      .disabled-info
        span(style="font-style: italic") Nur Gruppenmitglieder können Beiträge erstellen.
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  inject,
  PropType,
  Ref,
  ref,
  watch,
} from "vue"
import { ViewContext } from "concerns/useContextName"
import useMentionSearch from "concerns/useMentionSearch"
import { Attachment, Post } from "suus-api"
import GalleryUploader from "features/gallery/uploader/GalleryUploader.vue"
import { GalleryMedium } from "features/gallery/gallery.types"
import PostFormHeader from "features/posts/form/PostFormHeader.vue"
import PostFormButton from "features/posts/form/PostFormButton.vue"
import PostFormError from "features/posts/form/PostFormError.vue"
import { PostFormAddon } from "features/posts/form/addon/post-form-addon.types"
import {
  CreatePostFunction,
  DeletePostFunction,
  UpdatePostFunction,
} from "features/posts/form/post-form.types"
import GalleryAddon from "features/posts/form/gallery/GalleryAddon.vue"
import { GalleryService } from "features/gallery/service/gallery.service"
import ForumWysiwyg from "components/Wysiwyg/ForumWysiwyg.vue"
import AttachmentUploader from "features/attachments/AttachmentUploader.vue"
import ForumWysiwygWithUploader from "components/Wysiwyg/ForumWysiwygWithUploader.vue"
import WarningAlert from "app/ui-primitives/alerts/WarningAlert.vue"

export default defineComponent({
  name: "PostForm",
  components: {
    WarningAlert,
    ForumWysiwygWithUploader,
    AttachmentUploader,
    ForumWysiwyg,
    GalleryAddon,
    PostFormError,
    PostFormButton,
    PostFormHeader,
    GalleryUploader,
  },
  props: {
    value: {
      type: Object as PropType<Post>,
      default: null,
    },
    inline: { type: Boolean, default: false },
    isEdit: { type: Boolean, default: false },
    viewContext: { type: Object as PropType<ViewContext> },
    createPost: {
      type: Function as PropType<CreatePostFunction>,
    },
    updatePost: {
      type: Function as PropType<UpdatePostFunction>,
    },
    deletePost: {
      type: Function as PropType<DeletePostFunction>,
      required: false,
    },
    disabled: { type: Boolean, default: false },
    warning: { type: String, default: null },
  },
  setup(props, context) {
    /**
     * SETUP
     */
    const attachments = props.value?.attachments ?? []
    const isSaving = ref(false)
    const _postId = ref<number | undefined>(props.value?.id)

    watch(
      () => props.value?.id,
      (newVal) => (_postId.value = newVal)
    )

    const attachmentsValid = ref(true)
    const updateAttachmentsValid = (valid: boolean) => {
      attachmentsValid.value = valid
      if (validationCalledBefore.value) {
        validatePost(postText.value)
      }
    }

    const galleryService: GalleryService = inject("GalleryService", undefined)
    const galleryActive = computed(() => !!galleryService)

    const galleryAddon: Ref<PostFormAddon<GalleryMedium[]>> = ref(null)
    const registerGalleryAddon = (postAddon: PostFormAddon) => {
      galleryAddon.value = postAddon
    }

    const postText: Ref<string> = ref(props.value?.text ?? "")

    watch(
      () => props.value?.text ?? "",
      (newVal) => (postText.value = newVal)
    )

    const errorText: Ref<string> = ref("")
    const buttonDisabled: Ref<boolean> = computed(
      () => !!errorText.value.length || props.disabled
    )
    const postRules: ((v: string) => true | string)[] = [
      (v) => {
        const isEmpty = (!v && !v.length) || v.toString() === "<p></p>"
        return isEmpty ? "Bitte gib eine Antwort ein." : true
      },
    ]

    const validationCalledBefore = ref(false)

    // Only validate if there is an error text
    function revalidate() {
      if (errorText.value.length > 0) {
        validatePost(postText.value)
      }
    }
    watch(() => postText.value, revalidate)

    function validatePost(text: string): void {
      validationCalledBefore.value = true
      errorText.value = ""
      postRules.forEach((rule) => {
        const message = rule(text)
        if (typeof message === "string") {
          errorText.value += `${message} `
        }
      })
      if (!galleryValid.value) {
        errorText.value +=
          "Bitte überprüfe ob oder warte bis alle Dateien fehlerfrei hochgeladen wurden."
      }
      if (!attachmentsValid.value) {
        errorText.value +=
          "Bitte überprüfe ob oder warte bis alle Dateien fehlerfrei hochgeladen wurden."
      }
    }

    function resetForm() {
      postText.value = ""
      errorText.value = ""
      galleryAddon.value?.resetAddon()
    }

    async function savePost() {
      isSaving.value = true
      validatePost(postText.value)
      try {
        if (!buttonDisabled.value && postText.value) {
          if (props.isEdit) {
            await updatePost(postText.value)
          } else {
            await createPost(postText.value)
          }
        }
      } finally {
        isSaving.value = false
      }
    }

    async function createPost(text: string) {
      try {
        const createdPost = await createPostWithAttachmentsAndMedia(text)
        context.emit("postCreated", createdPost)
        resetForm()
      } catch (e) {
        if (e instanceof Error) {
          errorText.value = e.message
        } else {
          throw e
        }
      }
    }

    async function createPostWithAttachmentsAndMedia(
      text: string
    ): Promise<Post> {
      const createdPost = await props.createPost(text)

      _postId.value = createdPost.id

      if (galleryActive.value) {
        const createdGalleryMedia = await galleryAddon.value?.createAddonData(
          createdPost
        )
        createdPost.event_media_preview = createdGalleryMedia.slice(0, 11)
        createdPost.event_media_count = createdGalleryMedia.length
      }

      return createdPost
    }

    async function updatePost(text: string) {
      try {
        const updatedPost = await props.updatePost(props.value.id, text)

        if (galleryActive.value) {
          const updatedGalleryMedia = await galleryAddon.value?.updateAddonData(
            updatedPost
          )
          updatedPost.event_media_preview = updatedGalleryMedia.slice(0, 11)
          updatedPost.event_media_count = updatedGalleryMedia.length
        }

        context.emit("postUpdated", updatedPost)
      } catch (e) {
        if (e instanceof Error) {
          errorText.value = e.message
        }
      }
    }

    const { mentionableFunction: getMentionableFunc } = useMentionSearch(
      props.viewContext
    )

    const galleryValid = ref(true)
    const setGalleryValididity = (valid: boolean) =>
      (galleryValid.value = valid)

    const initialShowUploader = !!galleryService?.initialShowUploader

    const loadAttachment = async (frontendId: string): Promise<Attachment> => {
      const attachment = attachments.find(
        ({ frontend_id }) => frontend_id === frontendId
      )
      if (attachment) {
        return attachment
      } else {
        console.error("Attachment not found " + frontendId, attachments)
        throw "Attachment not found!"
      }
    }

    return {
      getMentionableFunc,
      savePost,
      errorText,
      buttonDisabled,
      postText,
      isSaving,
      setGalleryValididity,
      registerGalleryAddon,
      galleryActive,
      initialShowUploader,
      updateAttachmentsValid,
      loadAttachment,
    }
  },
})
</script>
